import { useMemo } from "react"

export type IconButtonVariant =
  | "white"
  | "outline"
  | "grey"
  | "primary"
  | "green"
  | "red"
  | "blur"
  | "transparent"
  | "transparent-white"
export type IconButtonSize = "xs" | "small" | "medium" | "large" | "xl"

interface UseIconButtonStylesProps {
  variant?: IconButtonVariant
  size?: IconButtonSize
  selected?: boolean
  iconType?: "outline" | "filled"
}

export const useIconButtonStyles = ({
  variant = "grey",
  size = "medium",
  selected = false,
  iconType = "outline",
}: UseIconButtonStylesProps) => {
  const styles = useMemo(() => {
    let backgroundColor = "bg-bg-2"
    let iconColor = "stroke-fg-1"
    let hover = "hover-enabled:hover:bg-bg-3"
    let border = "border-transparent"
    let _size = "size-[40px]"
    let selectedBg = "bg-bg-2"
    let selectedIconColor = "stroke-fg-1"

    const isOutlineIcon = iconType === "outline"

    switch (variant) {
      case "transparent-white":
        backgroundColor = "transparent"
        selectedBg = "transparent"
        hover = "hover-enabled:hover:bg-fg-1"
        iconColor = "stroke-bg-1"
        break
      case "transparent":
        backgroundColor = "transparent"
        selectedBg = "transparent"
        hover = "hover-enabled:hover:bg-bg-2"
        break
      case "white":
        backgroundColor = "bg-bg-1"
        selectedBg = "bg-bg-1"
        hover = "hover-enabled:hover:bg-bg-2"
        break
      case "outline":
        backgroundColor = "bg-bg-1"
        selectedBg = "bg-bg-1"
        hover = "hover-enabled:hover:bg-bg-2"
        border = "border-bg-3"
        break
      case "grey":
        backgroundColor = "bg-bg-2"
        selectedBg = "bg-bg-2"
        hover = "hover-enabled:hover:bg-bg-3"
        break
      case "primary":
        backgroundColor = "bg-fg-1"
        iconColor = isOutlineIcon ? "stroke-bg-1" : "fill-bg-1"
        selectedBg = "bg-fg-1"
        hover = "hover-enabled:hover:bg-primary-3"
        break
      case "green":
        backgroundColor = "bg-transparent"
        iconColor = isOutlineIcon ? "stroke-bg-1" : "fill-bg-1"
        border = "border-bg-3"
        selectedBg = "bg-green-default"
        hover = "hover-enabled:hover:bg-green-light"
        break
      case "red":
        border = "border-bg-3"
        iconColor = isOutlineIcon ? "stroke-bg-1" : "fill-bg-1"
        backgroundColor = "bg-red-default"
        selectedBg = "bg-red-default"
        hover = "hover-enabled:hover:bg-red-light"
        break
      case "blur":
        // Styles are added manually in main component
        iconColor = isOutlineIcon ? "stroke-bg-1" : "fill-bg-1"
        backgroundColor = ""
        selectedBg = ""
        hover = "hover-enabled:hover:"
        break
    }

    switch (size) {
      case "xs":
        _size = "size-[24px]"
        break
      case "small":
        _size = "size-[30px]"
        break
      case "medium":
        _size = "size-[38px]"
        break
      case "large":
        _size = "size-[46px]"
        break
      case "xl":
        _size = "size-[56px]"
        break
    }

    return {
      backgroundColor: selected ? selectedBg : backgroundColor,
      iconColor: selected ? selectedIconColor : iconColor,
      hover,
      border,
      _size,
    }
  }, [variant, size, selected, iconType])

  return styles
}
